import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import { Badge } from '@bliks-io/ui';
import type { PartnerMaterial, PartnerMaterialLocale } from '@bliks-io/contracts';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  isRecentlyChanged,
  pickPartnerMaterialText
} from '@/lib/partner-material-text';

interface InfoHubVideoCardProps {
  video: PartnerMaterial & { videoId: string };
  locale: PartnerMaterialLocale;
}

/**
 * Opens the video on YouTube instead of embedding it: the project's lint rule
 * requires a sandbox that forbids scripts together with same-origin, and the
 * YouTube player renders black under that sandbox (checked in Chromium). The
 * preview image comes from ytimg, which sets no cookies.
 */
export function InfoHubVideoCard({ video, locale }: InfoHubVideoCardProps) {
  const { t } = useTranslation();
  const title = pickPartnerMaterialText(video.title, locale);
  const description = pickPartnerMaterialText(video.description, locale);

  return (
    <figure className="flex flex-col gap-2">
      <a
        href={`https://www.youtube.com/watch?v=${video.videoId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block aspect-video w-full overflow-hidden rounded-lg border bg-muted"
        aria-label={t('partner.infoHub.playVideo', { title })}
      >
        <img
          src={`https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`}
          alt=""
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background/90 shadow transition-transform group-hover:scale-110">
            <Play className="h-5 w-5 text-foreground" />
          </span>
        </span>
      </a>
      <figcaption className="flex flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium">{title}</span>
          {isRecentlyChanged(video.changedAt) && (
            <Badge colorScheme="success" size="sm">
              {t('partner.infoHub.new')}
            </Badge>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(PARTNER_MATERIAL_CATEGORY_KEYS[video.category])}
        </span>
        {description && (
          <span className="line-clamp-2 text-xs text-muted-foreground">{description}</span>
        )}
      </figcaption>
    </figure>
  );
}
